import React from 'react'
import { CheckinFeature, Feature } from '../api/client'
import { resolveCollectionItemName } from '../utils/collectionUtils'
import { boolLabelFor } from '../utils/boolFeature'
import { PersonDetailValue } from './checkin/PersonDetailModal'
import { LocationDetailValue } from './checkin/LocationDetailModal'
import PersonAvatar from './PersonAvatar'

interface SubConfig {
    type?: string
    params?: any
}

export interface MapRefTap {
    collectionId: number
    itemId: number
}

interface MapValueCardProps {
    feature: Feature
    rows: CheckinFeature[]
    collectionItems: Record<string, any>
    onViewCheckins: () => void
    /** Close the surrounding popover before opening a follow-on modal. */
    onClose: () => void
    onPersonClick: (value: PersonDetailValue) => void
    onLocationClick: (value: LocationDetailValue) => void
    onGenericRefClick: (value: MapRefTap) => void
}

const renderScalar = (
    side: 'key' | 'value',
    row: CheckinFeature,
    sub: SubConfig,
    feature: Feature,
    collectionItems: Record<string, any>,
): React.ReactNode => {
    const t = sub?.type
    const params = sub?.params ?? {}

    if (t === 'reference') {
        const refId = side === 'key' ? row.key_reference_value : row.reference_value
        if (refId == null) return <span className="text-gray-400">?</span>
        const name = resolveCollectionItemName(refId, collectionItems) ?? `#${refId}`
        const item = collectionItems[String(refId)]
        if (item?.collection?.system_kind === 'people') {
            return (
                <span className="inline-flex items-center gap-1.5">
                    <PersonAvatar
                        data={item.data || {}}
                        className="h-4 w-4 shrink-0"
                        initialsClassName="text-[8px]"
                        alt={name}
                    />
                    <span className="truncate">{name}</span>
                </span>
            )
        }
        return <span>{name}</span>
    }

    if (t === 'num') {
        const num = side === 'key' ? row.key_decimal_value : row.decimal_value
        if (num == null) return <span className="text-gray-400">?</span>
        const unit = side === 'value' ? feature.unit || '' : ''
        return <span className="font-mono">{num}{unit}</span>
    }

    if (t === 'bool') {
        const num = side === 'key' ? row.key_decimal_value : row.decimal_value
        const label = boolLabelFor(num, params)
        if (label == null) return <span className="text-gray-400">?</span>
        return <span>{label}</span>
    }

    if (t === 'rating') {
        if (row.decimal_value == null) return <span className="text-gray-400">?</span>
        const max = params.max || 5
        return <span>{row.decimal_value}/{max}</span>
    }

    if (t === 'labeled_rating') {
        if (row.decimal_value == null) return <span className="text-gray-400">?</span>
        const labels: string[] = params.labels || []
        const label = labels[Math.floor(row.decimal_value) - 1]
        if (label) return <span>{label}</span>
        const max = params.max || 5
        return <span>{row.decimal_value}/{max}</span>
    }

    if (t === 'location') {
        if (row.reference_value != null) {
            const name = resolveCollectionItemName(row.reference_value, collectionItems)
            return <span>📍 {name ?? `#${row.reference_value}`}</span>
        }
        if (row.latitude != null && row.longitude != null) {
            return <span>📍 {row.latitude.toFixed(2)}°, {row.longitude.toFixed(2)}°</span>
        }
        return <span className="text-gray-400">?</span>
    }

    const free = side === 'key' ? row.key_freeform_value : row.freeform_value
    return <span>{free ?? <span className="text-gray-400">?</span>}</span>
}

const isReferenceCell = (
    side: 'key' | 'value',
    row: CheckinFeature,
    sub: SubConfig,
): boolean => {
    if (sub?.type === 'reference') {
        const refId = side === 'key' ? row.key_reference_value : row.reference_value
        return refId != null
    }
    // Location values that resolve to a saved Place also behave like references.
    if (side === 'value' && sub?.type === 'location' && row.reference_value != null) {
        return true
    }
    return false
}

const isCoordsCell = (
    side: 'key' | 'value',
    row: CheckinFeature,
    sub: SubConfig,
): boolean => {
    return (
        side === 'value' &&
        sub?.type === 'location' &&
        row.reference_value == null &&
        row.latitude != null &&
        row.longitude != null
    )
}

const Cell: React.FC<{
    side: 'key' | 'value'
    row: CheckinFeature
    sub: SubConfig
    feature: Feature
    collectionItems: Record<string, any>
    onPersonClick: (value: PersonDetailValue) => void
    onLocationClick: (value: LocationDetailValue) => void
    onGenericRefClick: (value: MapRefTap) => void
    onClose: () => void
}> = ({ side, row, sub, feature, collectionItems, onPersonClick, onLocationClick, onGenericRefClick, onClose }) => {
    const content = renderScalar(side, row, sub, feature, collectionItems)

    if (isReferenceCell(side, row, sub)) {
        const refId = (side === 'key' ? row.key_reference_value : row.reference_value)!
        const item = collectionItems[String(refId)]
        const sysKind: string | undefined = item?.collection?.system_kind
        const handle = () => {
            onClose()
            if (sysKind === 'people') {
                onPersonClick({ collectionItemId: refId, data: item?.data || {} })
                return
            }
            if (sysKind === 'places' || sub?.type === 'location') {
                const data = item?.data || {}
                onLocationClick({
                    collectionItemId: refId,
                    lat: data.lat ?? undefined,
                    lng: data.lng ?? undefined,
                    name: data.name ?? null,
                    address: data.address ?? null,
                    googlePlaceId: data.google_place_id ?? null,
                })
                return
            }
            const collectionId = sub?.params?.collection_id
            if (collectionId) onGenericRefClick({ collectionId, itemId: refId })
        }
        return (
            <button
                type="button"
                onClick={handle}
                className="text-amber-800 hover:text-amber-900 hover:underline cursor-pointer text-left"
            >
                {content}
            </button>
        )
    }

    if (isCoordsCell(side, row, sub)) {
        const handle = () => {
            onClose()
            onLocationClick({ lat: row.latitude!, lng: row.longitude! })
        }
        return (
            <button
                type="button"
                onClick={handle}
                className="text-amber-800 hover:text-amber-900 hover:underline cursor-pointer text-left"
            >
                {content}
            </button>
        )
    }

    return <>{content}</>
}

const MapValueCard: React.FC<MapValueCardProps> = ({
    feature,
    rows,
    collectionItems,
    onViewCheckins,
    onClose,
    onPersonClick,
    onLocationClick,
    onGenericRefClick,
}) => {
    const keySub: SubConfig = feature.config?.params?.key ?? {}
    const valueSub: SubConfig = feature.config?.params?.value ?? {}

    return (
        <div className="p-4 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[260px] max-w-[420px]">
            <h3 className="font-semibold text-gray-900 mb-3">{feature.title}</h3>

            <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                    {rows.length} pair{rows.length !== 1 ? 's' : ''}
                </p>

                <ul className="space-y-1 max-h-64 overflow-y-auto">
                    {rows.map((row, idx) => (
                        <li
                            key={idx}
                            className="grid grid-cols-[1fr_auto_1fr] gap-2 items-baseline px-2 py-1 bg-gray-50 rounded text-sm"
                        >
                            <div className="truncate">
                                <Cell
                                    side="key"
                                    row={row}
                                    sub={keySub}
                                    feature={feature}
                                    collectionItems={collectionItems}
                                    onPersonClick={onPersonClick}
                                    onLocationClick={onLocationClick}
                                    onGenericRefClick={onGenericRefClick}
                                    onClose={onClose}
                                />
                            </div>
                            <div className="text-gray-400 select-none">→</div>
                            <div className="truncate text-right">
                                <Cell
                                    side="value"
                                    row={row}
                                    sub={valueSub}
                                    feature={feature}
                                    collectionItems={collectionItems}
                                    onPersonClick={onPersonClick}
                                    onLocationClick={onLocationClick}
                                    onGenericRefClick={onGenericRefClick}
                                    onClose={onClose}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <button
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onViewCheckins()
                }}
                className="w-full px-4 py-2 bg-action text-plum rounded hover:bg-action transition-colors text-sm font-medium"
            >
                View checkins containing "{feature.title}"
            </button>
        </div>
    )
}

export default MapValueCard
